import React, { useMemo, useCallback, ReactElement } from 'react';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import Widget from '../../../components/widget/widget.component';
import ChipLink from '../../../components/chip-link/chip-link.component';
import HiddenBlock from '../../../components/hidden-block/hidden-block.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import FormFieldTextRich from '../../../components/form-field-text-rich/form-field-text-rich.component';
import FilteredText from '../../../components/filtered-text/filtered-text.component';
import RichTextBlock from '../../../components/rich-text-block/rich-text-block.component';
import { CONTACT_VIEW, DEAL_COMMON_EDIT } from '../../../constants/entity/permissions';
import { CONTACT_TYPE_COMPANY, CONTACT_TYPE_PERSON } from '../../../constants/entity/contact-types';
import CompanyDefaultImage from '../../../static/images/company-default-image.png';
import PersonDefaultImage from '../../../static/images/person-default-image.png';

const DealDescription: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const WIDGET_TITLE_DEFAULT: string = 'Deal Overview';
  const WIDGET_NAME: string = 'widget-deal-description';

  const {
    isEditMode,
    isNewDeal,
    dealData,
    dealParticipantsData,
    onDescriptionChange,
    onContactClick,
    onEditModeButtonClick,
  } = props;

  const dealCommonEditPermissions: string[] = useMemo((): string[] => [DEAL_COMMON_EDIT], []);

  const rawDealDescription: string = dealData && dealData.description;
  const formPrefix: string = 'dealData.dealCommonData';
  const isEmpty: boolean = !rawDealDescription && (!dealData || (dealData && !dealData.dealStructure
    && (!dealData.asset || !dealData.asset.portfolio) && !dealData.portfolio
    && (!dealData.sponsors || !dealData.sponsors.length)
    && (!dealData.brokers || !dealData.brokers.length)
    && (!dealData.lenders || !dealData.lenders.length)
    && !dealData.rejectReason && !dealData.rejectReasonComment
  ));
  const sponsorCompanyList: Contact[] = dealData && dealData.sponsors && dealData.sponsors.length
    ? dealData.sponsors.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_COMPANY) : [];
  const sponsorPersonList: Contact[] = dealData && dealData.sponsors && dealData.sponsors.length
    ? dealData.sponsors.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_PERSON) : [];
  const brokerCompanyList: Contact[] = dealData && dealData.brokers && dealData.brokers.length
    ? dealData.brokers.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_COMPANY) : [];
  const brokerPersonList: Contact[] = dealData && dealData.brokers && dealData.brokers.length
    ? dealData.brokers.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_PERSON) : [];
  const lenderCompanyList: Contact[] = dealData && dealData.lenders && dealData.lenders.length
    ? dealData.lenders.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_COMPANY) : [];
  const lenderPersonList: Contact[] = dealData && dealData.lenders && dealData.lenders.length
    ? dealData.lenders.filter((contactItem: Contact): boolean => contactItem.type === CONTACT_TYPE_PERSON) : [];

  const checkIsContactBelongsToCompanyList: Function = useCallback((
    contactItem: Contact, contactCompanyList: Contact[],
  ): boolean => (
    !!contactCompanyList.find((contactCompanyItem: Contact): boolean => (
      !!contactItem.company && contactCompanyItem.id === contactItem.company.id
    ))
  ), []);

  const getContactMap: Function = useCallback((
    contactList: Contact[], contactCompanyList: Contact[],
  ): Record<string, Contact[]> => (
    contactList.reduce((
      resultContactMap: Record<string, Contact[]>, contactItem: Contact,
    ): Record<string, Contact[]> => (
      contactItem.company && contactItem.company.id
      && checkIsContactBelongsToCompanyList(contactItem, contactCompanyList) ? {
        ...resultContactMap,
        [contactItem.company.id]: [
          ...(resultContactMap[contactItem.company.id] || []),
          contactItem,
        ],
      } : resultContactMap
    ), {})
  ), []);

  const companySponsorPersonMap: Record<string, Contact[]> = getContactMap(sponsorPersonList, sponsorCompanyList);
  const companyBrokerPersonMap: Record<string, Contact[]> = getContactMap(brokerPersonList, brokerCompanyList);
  const companyLenderPersonMap: Record<string, Contact[]> = getContactMap(lenderPersonList, lenderCompanyList);
  const sponsorPersonContactList: Contact[] = sponsorPersonList && sponsorPersonList.length
    ? sponsorPersonList.filter((contactItem: Contact): boolean => (
      !contactItem.company || (contactItem.company && (!contactItem.company.id
        || (!!contactItem.company.id && !checkIsContactBelongsToCompanyList(contactItem, sponsorCompanyList))))
    )) : [];
  const brokerPersonContactList: Contact[] = brokerPersonList && brokerPersonList.length
    ? brokerPersonList.filter((contactItem: Contact): boolean => (
      !contactItem.company || (contactItem.company && (!contactItem.company.id
        || (!!contactItem.company.id && !checkIsContactBelongsToCompanyList(contactItem, brokerCompanyList))))
    )) : [];
  const lenderPersonContactList: Contact[] = lenderPersonList && lenderPersonList.length
    ? lenderPersonList.filter((contactItem: Contact): boolean => (
      !contactItem.company || (contactItem.company && (!contactItem.company.id
        || (!!contactItem.company.id && !checkIsContactBelongsToCompanyList(contactItem, lenderCompanyList))))
    )) : [];

  return (
    <CheckAccess permissions={dealCommonEditPermissions} isEnabled={isEditMode || isNewDeal}>
      <Widget
        isFullWidth
        isFullHeight
        isEmpty={isEmpty && !isEditMode && !isNewDeal}
        isEditMode={isEditMode}
        withEditMode={!isNewDeal}
        editPermissions={dealCommonEditPermissions}
        widgetName={WIDGET_NAME}
        defaultWidgetTitle={WIDGET_TITLE_DEFAULT}
        onEditModeButtonClick={onEditModeButtonClick}
      >
        <HiddenBlock isActive={!isEditMode && !isNewDeal}>
          {
            (isEditMode || isNewDeal) && (
              <FormFieldTextRich name={`${formPrefix}.description`} onFieldChange={onDescriptionChange} />
            )
          }
        </HiddenBlock>
        {
          !isEditMode && !isNewDeal && (
            <Table>
              <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
                {
                  rawDealDescription && (
                    <TableRow>
                      <TableCell>
                        Description
                      </TableCell>
                      <TableCell>
                        <RichTextBlock>
                          <FilteredText text={rawDealDescription} />
                        </RichTextBlock>
                      </TableCell>
                    </TableRow>
                  )
                }
                {
                  dealData && ((dealData.asset && dealData.asset.portfolio) || dealData.portfolio) && (
                    <TableRow>
                      <TableCell>
                        Portfolio
                      </TableCell>
                      <TableCell>
                        {
                          (dealData.asset && dealData.asset.portfolio && dealData.asset.portfolio.name)
                          || (dealData.portfolio && dealData.portfolio.name)
                        }
                      </TableCell>
                    </TableRow>
                  )
                }
                {
                  sponsorCompanyList && !!sponsorCompanyList.length && (
                    <TableRow>
                      <TableCell>
                        Sponsors
                      </TableCell>
                      <TableCell>
                        {
                          sponsorCompanyList.map((contactItem: Contact): ReactElement => (
                            <ChipLink
                              isDisabled={isEditMode}
                              key={contactItem.id}
                              label={contactItem.fullName}
                              imageLink={contactItem.photoThumbnail || contactItem.photo || CompanyDefaultImage}
                              permission={CONTACT_VIEW}
                              onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                onContactClick(event, contactItem.id);
                              }}
                            />
                          ))
                        }
                      </TableCell>
                    </TableRow>
                  )
                }
                {
                  sponsorCompanyList && !!sponsorCompanyList.length
                  && sponsorCompanyList.map((contactItem: Contact): ReactElement | boolean => (
                    !!contactItem.id && !!contactItem.name && companySponsorPersonMap
                    && !!companySponsorPersonMap[contactItem.id] && (
                      <TableRow key={contactItem.id}>
                        <TableCell>
                          Sponsor
                          {' '}
                          {contactItem.name}
                          {' '}
                          Staff
                        </TableCell>
                        <TableCell>
                          {
                            companySponsorPersonMap[contactItem.id].map((personContactItem: Contact): ReactElement => (
                              <ChipLink
                                isDisabled={isEditMode}
                                key={personContactItem.id}
                                label={personContactItem.fullName}
                                imageLink={personContactItem.photoThumbnail || personContactItem.photo
                                  || CompanyDefaultImage}
                                permission={CONTACT_VIEW}
                                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                  onContactClick(event, personContactItem.id);
                                }}
                              />
                            ))
                          }
                        </TableCell>
                      </TableRow>
                    )
                  ))
                }
                {
                  sponsorPersonContactList && !!sponsorPersonContactList.length && (
                    <TableRow>
                      <TableCell>
                        {
                          companySponsorPersonMap && !!Object.keys(companySponsorPersonMap).length
                          && `Other `
                        }
                        Sponsor Contacts
                      </TableCell>
                      <TableCell>
                        {
                          sponsorPersonContactList.map((contactItem: Contact): ReactElement => (
                            <ChipLink
                              isDisabled={isEditMode}
                              key={contactItem.id}
                              label={contactItem.fullName}
                              imageLink={contactItem.photoThumbnail || contactItem.photo || PersonDefaultImage}
                              permission={CONTACT_VIEW}
                              onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                onContactClick(event, contactItem.id);
                              }}
                            />
                          ))
                        }
                      </TableCell>
                    </TableRow>
                  )
                }
                {
                  brokerCompanyList && !!brokerCompanyList.length && (
                    <TableRow>
                      <TableCell>
                        Brokers
                      </TableCell>
                      <TableCell>
                        {
                          brokerCompanyList.map((contactItem: Contact): ReactElement => (
                            <ChipLink
                              isDisabled={isEditMode}
                              key={contactItem.id}
                              label={contactItem.fullName}
                              imageLink={contactItem.photoThumbnail || contactItem.photo || CompanyDefaultImage}
                              permission={CONTACT_VIEW}
                              onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                onContactClick(event, contactItem.id);
                              }}
                            />
                          ))
                        }
                      </TableCell>
                    </TableRow>
                  )
                }
                {
                  brokerCompanyList && !!brokerCompanyList.length
                  && brokerCompanyList.map((contactItem: Contact): ReactElement | boolean => (
                    !!contactItem.id && !!contactItem.name && companyBrokerPersonMap
                    && !!companyBrokerPersonMap[contactItem.id] && (
                      <TableRow key={contactItem.id}>
                        <TableCell>
                          Broker
                          {' '}
                          {contactItem.name}
                          {' '}
                          Staff
                        </TableCell>
                        <TableCell>
                          {
                            companyBrokerPersonMap[contactItem.id].map((personContactItem: Contact): ReactElement => (
                              <ChipLink
                                isDisabled={isEditMode}
                                key={personContactItem.id}
                                label={personContactItem.fullName}
                                imageLink={personContactItem.photoThumbnail || personContactItem.photo
                                  || CompanyDefaultImage}
                                permission={CONTACT_VIEW}
                                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                  onContactClick(event, personContactItem.id);
                                }}
                              />
                            ))
                          }
                        </TableCell>
                      </TableRow>
                    )
                  ))
                }
                {
                  brokerPersonContactList && !!brokerPersonContactList.length && (
                    <TableRow>
                      <TableCell>
                        {
                          companyBrokerPersonMap && !!Object.keys(companyBrokerPersonMap).length
                          && `Other `
                        }
                        Broker Contacts
                      </TableCell>
                      <TableCell>
                        {
                          brokerPersonContactList.map((contactItem: Contact): ReactElement => (
                            <ChipLink
                              isDisabled={isEditMode}
                              key={contactItem.id}
                              label={contactItem.fullName}
                              imageLink={contactItem.photoThumbnail || contactItem.photo || PersonDefaultImage}
                              permission={CONTACT_VIEW}
                              onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                onContactClick(event, contactItem.id);
                              }}
                            />
                          ))
                        }
                      </TableCell>
                    </TableRow>
                  )
                }
                {
                  lenderCompanyList && !!lenderCompanyList.length && (
                    <TableRow>
                      <TableCell>
                        Lenders
                      </TableCell>
                      <TableCell>
                        {
                          lenderCompanyList.map((contactItem: Contact): ReactElement => (
                            <ChipLink
                              isDisabled={isEditMode}
                              key={contactItem.id}
                              label={contactItem.fullName}
                              imageLink={contactItem.photoThumbnail || contactItem.photo || CompanyDefaultImage}
                              permission={CONTACT_VIEW}
                              onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                onContactClick(event, contactItem.id);
                              }}
                            />
                          ))
                        }
                      </TableCell>
                    </TableRow>
                  )
                }
                {
                  lenderCompanyList && !!lenderCompanyList.length
                  && lenderCompanyList.map((contactItem: Contact): ReactElement | boolean => (
                    !!contactItem.id && !!contactItem.name && companyLenderPersonMap
                    && !!companyLenderPersonMap[contactItem.id] && (
                      <TableRow key={contactItem.id}>
                        <TableCell>
                          Lender
                          {' '}
                          {contactItem.name}
                          {' '}
                          Staff
                        </TableCell>
                        <TableCell>
                          {
                            companyLenderPersonMap[contactItem.id].map((personContactItem: Contact): ReactElement => (
                              <ChipLink
                                isDisabled={isEditMode}
                                key={personContactItem.id}
                                label={personContactItem.fullName}
                                imageLink={personContactItem.photoThumbnail || personContactItem.photo
                                  || CompanyDefaultImage}
                                permission={CONTACT_VIEW}
                                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                  onContactClick(event, personContactItem.id);
                                }}
                              />
                            ))
                          }
                        </TableCell>
                      </TableRow>
                    )
                  ))
                }
                {
                  lenderPersonContactList && !!lenderPersonContactList.length && (
                    <TableRow>
                      <TableCell>
                        {
                          companyLenderPersonMap && !!Object.keys(companyLenderPersonMap).length
                          && `Other `
                        }
                        Lender Contacts
                      </TableCell>
                      <TableCell>
                        {
                          lenderPersonContactList.map((contactItem: Contact): ReactElement => (
                            <ChipLink
                              isDisabled={isEditMode}
                              key={contactItem.id}
                              label={contactItem.fullName}
                              imageLink={contactItem.photoThumbnail || contactItem.photo || CompanyDefaultImage}
                              permission={CONTACT_VIEW}
                              onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                onContactClick(event, contactItem.id);
                              }}
                            />
                          ))
                        }
                      </TableCell>
                    </TableRow>
                  )
                }
                {
                  dealData && dealData.newDealActivity && dealData.newDealActivity.dealContacts
                  && !!dealData.newDealActivity.dealContacts.length && (
                    <TableRow>
                      <TableCell>
                        Deal Lead
                      </TableCell>
                      <TableCell>
                        {
                          dealData.newDealActivity.dealContacts.map((contactItem: Contact): ReactElement => (
                            <ChipLink
                              isDisabled={isEditMode}
                              key={contactItem.id}
                              label={contactItem.fullName}
                              imageLink={contactItem.photoThumbnail || contactItem.photo
                                || PersonDefaultImage}
                              permission={CONTACT_VIEW}
                              onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                onContactClick(event, contactItem.id);
                              }}
                            />
                          ))
                        }
                      </TableCell>
                    </TableRow>
                  )
                }
                {
                  dealParticipantsData && !!dealParticipantsData.length && (
                    <TableRow>
                      <TableCell>
                        Deal Team
                      </TableCell>
                      <TableCell>
                        {
                          dealParticipantsData.map((dealParticipantData: DealParticipant): ReactElement | null => (
                            !!dealParticipantData.contact && (
                              <ChipLink
                                isDisabled={isEditMode}
                                key={dealParticipantData.contact.id}
                                label={dealParticipantData.contact.fullName}
                                imageLink={dealParticipantData.contact.photoThumbnail
                                  || dealParticipantData.contact.photo || PersonDefaultImage}
                                permission={CONTACT_VIEW}
                                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                  onContactClick(event, dealParticipantData.contact.id);
                                }}
                              />
                            )
                          ))
                        }
                      </TableCell>
                    </TableRow>
                  )
                }
                {
                  dealData && dealData.dealStructure && (
                    <TableRow>
                      <TableCell>
                        Deal Structure
                      </TableCell>
                      <TableCell>
                        {dealData.dealStructure}
                      </TableCell>
                    </TableRow>
                  )
                }
                {
                  dealData && dealData.rejectReason && (
                    <TableRow>
                      <TableCell>
                        Dead Deal Reason
                      </TableCell>
                      <TableCell>
                        {dealData.rejectReason}
                      </TableCell>
                    </TableRow>
                  )
                }
                {
                  dealData && dealData.rejectReasonComment && (
                    <TableRow>
                      <TableCell>
                        Dead Deal Reason Comment
                      </TableCell>
                      <TableCell>
                        {dealData.rejectReasonComment}
                      </TableCell>
                    </TableRow>
                  )
                }
              </TableBody>
            </Table>
          )
        }
      </Widget>
    </CheckAccess>
  );
});

export default DealDescription;
